import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import PhoneInput from '../PhoneInput';
import { Label } from '../ui/label';
import { isValidEmail, isValidPhoneDigits } from '../../lib/validation';

const defaultMessage = (category, zone) => {
  // Phase 86AZ.12 — Zone-aware invite copy. Green Zone customers shouldn't
  // see "Blue Zone platformuna" in their invitation message; pivot to the
  // matching label so the invited collaborator sees the right context.
  const zoneLabel = zone === 'green_zone' ? 'Green Zone' : 'Blue Zone';
  return `COP 31 ${zoneLabel} platformuna ${category || 'ilgili kategori'} kapsamında davet ediliyorsunuz.`;
};

export const InvitationForm = ({ categories, onSubmit, onClose, zone = 'blue_zone' }) => {
  const [form, setForm] = useState({
    invitee_name: '',
    email: '',
    phone_code: '+90',
    phone_number: '',
    category_id: '',
    category_name: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleCategoryChange = (id) => {
    const cat = categories.find((c) => c.id === id);
    setForm((prev) => ({
      ...prev,
      category_id: id,
      category_name: cat?.name || '',
    }));
    setErrors((prev) => ({ ...prev, category_id: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.invitee_name.trim()) next.invitee_name = 'Ad soyad zorunludur.';
    if (!form.email.trim()) next.email = 'E-posta zorunludur.';
    else if (!isValidEmail(form.email)) next.email = 'Geçerli bir e-posta girin (örn. ad@firma.com).';
    if (form.phone_number && !isValidPhoneDigits(form.phone_number, { min: 7, max: 15 })) {
      next.phone_number = 'Telefon 7–15 rakam içermelidir.';
    }
    if (!form.category_id) next.category_id = 'Kategori seçiniz.';
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = validate();
    if (Object.keys(next).length > 0) { setErrors(next); return; }
    setSubmitting(true);
    setServerError('');
    try {
      await onSubmit({
        email: form.email.trim(),
        invitee_name: form.invitee_name.trim(),
        phone_code: form.phone_code,
        phone_number: form.phone_number,
        category_id: form.category_id,
        category_name: form.category_name,
      });
      onClose();
    } catch (err) {
      setServerError(err.response?.data?.detail || 'Davet gönderilemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-500 px-8 py-5 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-white">Kullanıcı Davet Et</h2>
            <p className="text-emerald-100 text-sm mt-0.5">Davet linki e-posta ile gönderilir</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
          {serverError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
              {serverError}
            </div>
          )}

          {/* Category */}
          <div className="space-y-1.5">
            <Label>Kategori <span className="text-red-500">*</span></Label>
            <select
              data-testid="invite-category-select"
              value={form.category_id}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Kategori seçin...</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {errors.category_id && <p className="text-red-500 text-xs">{errors.category_id}</p>}
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <Label>Ad Soyad <span className="text-red-500">*</span></Label>
            <Input
              data-testid="invite-name-input"
              placeholder="Ahmet Yılmaz"
              value={form.invitee_name}
              onChange={(e) => setField('invitee_name', e.target.value)}
              className="rounded-xl"
            />
            {errors.invitee_name && <p className="text-red-500 text-xs">{errors.invitee_name}</p>}
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <Label>E-posta <span className="text-red-500">*</span></Label>
            <Input
              data-testid="invite-email-input"
              type="email"
              placeholder="kisi@firma.com"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              className="rounded-xl"
            />
            {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <Label>Cep Telefonu</Label>
            <PhoneInput
              codeValue={form.phone_code}
              numberValue={form.phone_number}
              onCodeChange={(v) => setField('phone_code', v)}
              onNumberChange={(v) => setField('phone_number', v)}
            />
            {errors.phone_number && <p className="text-red-500 text-xs">{errors.phone_number}</p>}
          </div>

          {/* Auto-message preview */}
          {form.category_name && (
            <div className="bg-emerald-50 rounded-xl p-3 text-sm text-emerald-800 border border-emerald-100">
              <span className="font-medium">Davet mesajı: </span>
              {defaultMessage(form.category_name, zone)}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={onClose}>
              İptal
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="invite-submit-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {submitting ? 'Gönderiliyor...' : 'Davet Gönder'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
